import React, { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Loader2, Download, FileSignature, X, MapPin, Save } from "lucide-react";

const STATUS_OPTS = [
  { key: "", label: "Semua Data" },
  { key: "counted", label: "Sudah Dihitung" },
  { key: "uncounted", label: "Belum Dihitung" },
  { key: "diff", label: "Ada Selisih" },
  { key: "minus", label: "Selisih Minus (-)" },
  { key: "plus", label: "Selisih Plus (+)" },
];

export default function Reports() {
  const [locs, setLocs] = useState([]);
  const [exportLocs, setExportLocs] = useState([]);
  const [lokasi, setLokasi] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState("");
  const [showBA, setShowBA] = useState(false);

  useEffect(() => {
    api.get("/locations").then((r) => setLocs(r.data)).catch(() => {});
    api.get("/master-lokasi-list").then((r) => setExportLocs(r.data)).catch(() => {});
  }, []);

  const doExport = async (type) => {
    setBusy(type);
    try {
      const res = await api.get(`/export/${type}`, {
        params: { lokasi, status },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "excel" ? "Stock_Opname_Export.xlsx" : "Laporan_Stock_Opname.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Laporan ${type === "excel" ? "Excel" : "PDF"} berhasil diunduh`);
    } catch (e) {
      toast.error("Gagal mengekspor laporan");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="pb-12">
      <PageHeader title="Laporan / Export" subtitle="Ekspor hasil stock opname sesuai format template" />

      <div className="px-6 lg:px-8 pt-6 max-w-3xl space-y-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-head font-bold text-slate-800 mb-4">Filter Data Export</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Tempat Penyimpanan</label>
              <select
                data-testid="export-lokasi-select"
                value={lokasi}
                onChange={(e) => setLokasi(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600"
              >
                <option value="">Semua Tempat</option>
                {exportLocs.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Status</label>
              <select
                data-testid="export-status-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600"
              >
                {STATUS_OPTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-4 leading-relaxed">
            Kolom export mengikuti template: ID Inventory, ID Barang, Nama, ID Produsen, Produsen, ID Gudang,
            Gudang, ID Lokasi penyimpanan, Lokasi penyimpanan, Harga Pokok, Satuan Jual, Quantity Awal, Selisih.
            Nilai selisih minus ditampilkan dengan tanda minus (mis. <span className="font-mono-t text-red-600 font-bold">-5</span>).
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            onClick={() => doExport("excel")}
            disabled={busy}
            data-testid="export-excel-button"
            className="group bg-white rounded-xl border border-slate-200 p-6 text-left hover:border-emerald-300 hover:shadow-sm transition-all disabled:opacity-60"
          >
            <div className="flex items-center justify-between">
              <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                {busy === "excel" ? <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" /> : <FileSpreadsheet className="h-6 w-6 text-emerald-600" />}
              </div>
              <Download className="h-5 w-5 text-slate-300 group-hover:text-emerald-500 transition-colors" />
            </div>
            <h4 className="mt-4 font-head font-bold text-slate-900">Export Excel (.xlsx)</h4>
            <p className="text-xs text-slate-500 mt-1">Format tabel sesuai template, siap diolah lebih lanjut.</p>
          </button>

          <button
            onClick={() => doExport("pdf")}
            disabled={busy}
            data-testid="export-pdf-button"
            className="group bg-white rounded-xl border border-slate-200 p-6 text-left hover:border-red-300 hover:shadow-sm transition-all disabled:opacity-60"
          >
            <div className="flex items-center justify-between">
              <div className="h-12 w-12 rounded-xl bg-red-50 flex items-center justify-center">
                {busy === "pdf" ? <Loader2 className="h-6 w-6 text-red-600 animate-spin" /> : <FileText className="h-6 w-6 text-red-600" />}
              </div>
              <Download className="h-5 w-5 text-slate-300 group-hover:text-red-500 transition-colors" />
            </div>
            <h4 className="mt-4 font-head font-bold text-slate-900">Export PDF</h4>
            <p className="text-xs text-slate-500 mt-1">Laporan siap cetak dengan header rumah sakit.</p>
          </button>

          <button
            onClick={() => setShowBA(true)}
            data-testid="open-berita-acara-button"
            className="group bg-white rounded-xl border border-slate-200 p-6 text-left hover:border-teal-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center">
                <FileSignature className="h-6 w-6 text-teal-700" />
              </div>
              <Download className="h-5 w-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
            </div>
            <h4 className="mt-4 font-head font-bold text-slate-900">Cetak Berita Acara</h4>
            <p className="text-xs text-slate-500 mt-1">Berita acara kegiatan stok opname sesuai template resmi.</p>
          </button>
        </div>
      </div>

      {showBA && <BeritaAcaraModal locs={exportLocs} onClose={() => setShowBA(false)} />}
    </div>
  );
}

const DEF_TUJUAN = `Untuk mengetahui fisik real data stok perbekalan farmasi.
Tersedianya data awal stok gudang farmasi, unit Farmasi, trolley emergency dan lemari obat igd, ebony, poliklinik sebagai acuan stok opname berikutnya.`;
const DEF_KENDALA = `Penginputan trolley emergency dan lemari obat tidak dilakukan real time karena petugas penginputan melakukan perhitungan terlebih dahulu.
Penginputan pada sistem terkendala karena pembuatan excel khusus trolley belum disiapkan oleh IT.
Penginputan trolley emergency dan lemari obat kurang teliti karena terburu-buru, sehingga ditemukan banyak kekurangan penginputan.`;
const DEF_TINDAK = `Petugas bagian penginputan khusus melakukan penginputan tidak melakukan perhitungan.
Monitoring pencatatan keluar masuk barang pada kartu stok dan penginputan kedalam sistem komputer secara real time.
Monitoring pencatatan pada kartu stok.`;

function BeritaAcaraModal({ locs, onClose }) {
  const todayID = new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    subtitle: "UNIT FARMASI, TROLEY EMERGENCY, GUDANG FARMASI, DEPO OK",
    so_lokasi: "", waktu_pelaksanaan: "", jam_perhitungan: "", jumlah_petugas: "",
    ka_instalasi: "", manager_jangmed: "", manager_keuangan: "", team_it: "",
    tujuan: DEF_TUJUAN, kendala: DEF_KENDALA, tindak_lanjut: DEF_TINDAK,
    kota: "Tangerang", tanggal: todayID, dibuat_oleh: "", dibuat_jabatan: "Koordinator Unit Farmasi",
    ttd_ka_instalasi: "", ttd_jangmed: "", ttd_manager: "", ttd_it: "", direktur: "", direktur_jabatan: "Direktur RS Mulya",
    locations: [],
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleLoc = (nama) => setF((p) => ({
    ...p,
    locations: p.locations.includes(nama) ? p.locations.filter((x) => x !== nama) : [...p.locations, nama],
  }));

  const [drafts, setDrafts] = useState([]);
  const [selDraft, setSelDraft] = useState("");
  const [activeDraft, setActiveDraft] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const loadDrafts = () => api.get("/berita-acara/drafts").then((r) => setDrafts(r.data)).catch(() => {});
  useEffect(() => { loadDrafts(); }, []);

  const loadDraft = async () => {
    if (!selDraft) { toast.error("Pilih draf dulu"); return; }
    try {
      const { data } = await api.get(`/berita-acara/drafts/${selDraft}`);
      const { id, name, updated_at, ...fields } = data;
      setF((p) => ({ ...p, ...fields }));
      setActiveDraft({ id, name });
      toast.success(`Draf "${name}" dimuat`);
    } catch (e) { toast.error("Gagal memuat draf"); }
  };

  const saveNewDraft = async () => {
    const name = window.prompt("Nama draf (mis. SO Januari 2026):", "");
    if (!name || !name.trim()) return;
    setSavingDraft(true);
    try {
      const { data } = await api.post("/berita-acara/drafts", { ...f, name: name.trim() });
      toast.success("Draf baru disimpan");
      setActiveDraft({ id: data.id, name: data.name });
      setSelDraft(data.id);
      loadDrafts();
    } catch (e) { toast.error("Gagal menyimpan draf"); }
    finally { setSavingDraft(false); }
  };

  const updateDraft = async () => {
    if (!activeDraft) return;
    setSavingDraft(true);
    try {
      await api.put(`/berita-acara/drafts/${activeDraft.id}`, { ...f, name: activeDraft.name });
      toast.success(`Draf "${activeDraft.name}" diperbarui`);
      loadDrafts();
    } catch (e) { toast.error("Gagal memperbarui draf"); }
    finally { setSavingDraft(false); }
  };

  const deleteDraft = async () => {
    if (!selDraft) { toast.error("Pilih draf dulu"); return; }
    if (!window.confirm("Hapus draf ini?")) return;
    try {
      await api.delete(`/berita-acara/drafts/${selDraft}`);
      toast.success("Draf dihapus");
      if (activeDraft?.id === selDraft) setActiveDraft(null);
      setSelDraft("");
      loadDrafts();
    } catch (e) { toast.error("Gagal menghapus draf"); }
  };

  const inp = (k, label, opts = {}) => (
    <div className={opts.span ? "col-span-2" : ""}>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      {opts.area ? (
        <textarea value={f[k]} onChange={(e) => set(k, e.target.value)} rows={opts.rows || 3} data-testid={`ba-${k}-input`}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
      ) : (
        <input value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={opts.ph || ""} data-testid={`ba-${k}-input`}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
      )}
    </div>
  );

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post("/berita-acara/pdf", f, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Berita_Acara_Stock_Opname.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Berita acara berhasil dibuat");
      onClose();
    } catch (e) { toast.error("Gagal membuat berita acara"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5 sticky top-0 bg-white">
          <h3 className="font-head text-lg font-bold text-slate-900">Cetak Berita Acara Stock Opname</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <label className="text-xs font-medium text-slate-600">Draf Tersimpan</label>
            <div className="mt-1.5 flex gap-2">
              <select value={selDraft} onChange={(e) => setSelDraft(e.target.value)} data-testid="draft-select"
                className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600">
                <option value="">— pilih draf tersimpan —</option>
                {drafts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <button type="button" onClick={loadDraft} data-testid="load-draft-button" className="px-3 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold">Muat</button>
              <button type="button" onClick={deleteDraft} data-testid="delete-draft-button" className="px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50">Hapus</button>
            </div>
            {activeDraft && <p className="text-[11px] text-teal-700 mt-1.5">Draf aktif: <b>{activeDraft.name}</b></p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {inp("subtitle", "Subjudul (unit)", { span: true })}
            {inp("waktu_pelaksanaan", "Waktu Pelaksanaan", { ph: "cth: 15 Januari 2026" })}
            {inp("jam_perhitungan", "Jam Perhitungan", { ph: "cth: 08.00 - 12.00 WIB" })}
            {inp("jumlah_petugas", "Jumlah Petugas Perhitungan/Input")}
            {inp("ka_instalasi", "Ka Instalasi Rajal")}
            {inp("manager_jangmed", "Manager Pelayanan Penunjang Medis")}
            {inp("manager_keuangan", "Manager Keuangan")}
            {inp("team_it", "Team IT")}
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Tempat Penyimpanan yang Dilaporkan</label>
            <p className="text-[11px] text-slate-400 mb-1.5">Diambil dari kolom Tempat Penyimpanan di Master Obat. Kosongkan untuk menyertakan semua. Hasil SO di-breakdown per tempat penyimpanan.</p>
            <div className="max-h-36 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
              {locs.length === 0 && <p className="px-3 py-3 text-xs text-slate-400">Belum ada data tempat penyimpanan di master obat.</p>}
              {locs.map((name) => (
                <label key={name} className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50" data-testid={`ba-loc-${name}`}>
                  <input type="checkbox" checked={f.locations.includes(name)} onChange={() => toggleLoc(name)} className="rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                  <MapPin className="h-3.5 w-3.5 text-slate-400" /> {name}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {inp("tujuan", "Tujuan Kegiatan (1 baris = 1 poin)", { area: true, span: true })}
            {inp("kendala", "Kendala / Masalah (1 baris = 1 poin)", { area: true, span: true })}
            {inp("tindak_lanjut", "Rencana Tindak Lanjut (1 baris = 1 poin)", { area: true, span: true })}
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            {inp("kota", "Kota")}
            {inp("tanggal", "Tanggal Berita Acara")}
            {inp("dibuat_oleh", "Dibuat Oleh (Nama)")}
            {inp("dibuat_jabatan", "Jabatan Pembuat")}
            {inp("ttd_ka_instalasi", "TTD Ka. Inst. Rajal")}
            {inp("ttd_jangmed", "TTD Manager Pel. Penunjang Medis")}
            {inp("ttd_manager", "TTD Manager Keuangan")}
            {inp("ttd_it", "TTD Koor. Unit IT")}
            {inp("direktur", "Nama Direktur")}
            {inp("direktur_jabatan", "Jabatan Direktur", { span: true })}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2 sticky bottom-0 bg-white pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Batal</button>
          {activeDraft && (
            <button onClick={updateDraft} disabled={savingDraft} data-testid="update-draft-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-teal-600 text-teal-700 text-sm font-semibold hover:bg-teal-50 disabled:opacity-60">
              {savingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Perbarui Draf
            </button>
          )}
          <button onClick={saveNewDraft} disabled={savingDraft} data-testid="save-berita-draft-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-teal-600 text-teal-700 text-sm font-semibold hover:bg-teal-50 disabled:opacity-60">
            {savingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan sebagai Draf
          </button>
          <button onClick={generate} disabled={busy} data-testid="generate-berita-acara-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} Buat & Unduh PDF
          </button>
        </div>
      </div>
    </div>
  );
}
